import { useState } from "react";
import { useParams } from "react-router-dom";
import { observer } from "mobx-react-lite";

import { useSetOrRemoveAzureKeys } from "../../../queries";
import LoadingSpinner from "../../../components/LoadingSpinner";

import { AzureApiKeys } from "./types";
import { useApiKeyStore } from "./store/api-key-store";
import { RevealableInput } from "./RevealableInput";

import { useAuth } from "@/context/auth-context";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

const LoadingSkeleton = () => (
  <div className="max-w-[600px]">
    <div className="mt-1 flex flex-col gap-2">
      <div className="flex w-full flex-col gap-1">
        <Skeleton className="h-5 w-full max-w-[100px]" />
        <Skeleton className="h-9 w-full max-w-[600px]" />
      </div>
      <div className="flex w-full flex-col gap-1">
        <Skeleton className="h-5 w-full max-w-[110px]" />
        <Skeleton className="h-9 w-full max-w-[600px]" />
      </div>
      <div className="flex w-full flex-col gap-1">
        <Skeleton className="h-5 w-full max-w-[120px]" />
        <Skeleton className="h-9 w-full max-w-[600px]" />
      </div>
      <div className="mt-2 flex w-full">
        <Skeleton className="h-10 w-[190px]" />
      </div>
    </div>
  </div>
);

interface AzureSectionProps {
  onAfterSaveSuccess?: () => void;
}

const AzureSection = observer(({ onAfterSaveSuccess }: AzureSectionProps) => {
  const auth = useAuth();
  const { workspaceId } = useParams();
  const store = useApiKeyStore();
  const hasAzureApiKey = store.getApiKey("openai.azure");
  const isStoreLoading = store.isLoading;

  const [azureApiKey, setAzureApiKey] = useState<AzureApiKeys>({
    azure_api_key: "",
    azure_endpoint: "",
    azure_api_version: "",
  });

  const azureMutation = useSetOrRemoveAzureKeys(auth?.userToken ?? "");

  const saveAzureKeys = async (azureKeys: AzureApiKeys | null) => {
    const response = await azureMutation.mutateAsync({
      ...azureKeys,
      workspace_id: Number(workspaceId),
    });
    if (response.success) {
      store.setApiKey("openai.azure", !!azureKeys);
      store.setApiKeyConfig("azure_endpoint", azureKeys?.azure_endpoint ?? "");
      store.setApiKeyConfig("azure_api_version", azureKeys?.azure_api_version ?? "");
      store.setApiKeyConfig("azure_api_key_suffix", azureKeys?.azure_api_key?.slice(-4) ?? "");
      setAzureApiKey({
        azure_api_key: "",
        azure_endpoint: "",
        azure_api_version: "",
      });
      if (azureKeys) {
        onAfterSaveSuccess?.();
      }
    }
  };

  const removeApiKey = async () => {
    await saveAzureKeys(null);
  };

  if (isStoreLoading) {
    return <LoadingSkeleton />;
  }

  const isFormValid =
    azureApiKey.azure_api_key && azureApiKey.azure_endpoint && azureApiKey.azure_api_version;

  const azureKeySuffix = store.getApiKeyConfig("azure_api_key_suffix");
  const azureKeyDisplay = azureKeySuffix
    ? `••••••••••••••••••••${azureKeySuffix}`
    : "••••••••••••••••••••••••";

  return (
    <div className="mt-2 flex h-full flex-col">
      <div className="mt-1 flex max-w-[600px] flex-col gap-2">
        <Input
          label="Azure API Key"
          name="azure_api_key"
          autoComplete="off"
          type={hasAzureApiKey ? "text" : "password"}
          id="azure_api_key"
          placeholder="Enter Azure API Key..."
          value={hasAzureApiKey ? azureKeyDisplay : azureApiKey.azure_api_key}
          readOnly={!!hasAzureApiKey}
          className={hasAzureApiKey ? "font-mono" : undefined}
          onChange={e =>
            setAzureApiKey({
              ...azureApiKey,
              azure_api_key: e.target.value,
            })
          }
        />
        <RevealableInput
          label="Azure Endpoint"
          name="azure_endpoint"
          id="azure_endpoint"
          placeholder="Enter Azure Endpoint..."
          value={
            hasAzureApiKey ? store.getApiKeyConfig("azure_endpoint") : azureApiKey.azure_endpoint
          }
          readOnly={!!hasAzureApiKey}
          onChange={e =>
            setAzureApiKey({
              ...azureApiKey,
              azure_endpoint: e.target.value,
            })
          }
        />
        <RevealableInput
          label="Azure API Version"
          name="azure_api_version"
          id="azure_api_version"
          placeholder="Enter Azure API Version..."
          value={
            hasAzureApiKey
              ? store.getApiKeyConfig("azure_api_version")
              : azureApiKey.azure_api_version
          }
          readOnly={!!hasAzureApiKey}
          onChange={e =>
            setAzureApiKey({
              ...azureApiKey,
              azure_api_version: e.target.value,
            })
          }
        />

        <div className="mt-2">
          {!hasAzureApiKey ? (
            <Button
              onClick={() => saveAzureKeys(azureApiKey)}
              disabled={!isFormValid || azureMutation.isPending}
              variant="outline"
            >
              {azureMutation.isPending ? <LoadingSpinner size={5} /> : "Save Azure Credentials"}
            </Button>
          ) : (
            <Button
              variant="destructiveOutline"
              onClick={removeApiKey}
              disabled={azureMutation.isPending}
            >
              Remove Azure Key
            </Button>
          )}
        </div>
      </div>
    </div>
  );
});

export default AzureSection;
